import { useEffect } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { brandingConfig } from '@/config/branding';
import { useAppBranding, useIsAppBrandingReady } from '@/hooks/use-app-branding';
import { useDevTabStoreName } from '@/hooks/use-dev-tab-store';
import { useResolveSignInBrand } from '@/hooks/use-resolve-sign-in-brand';
import { useSafeRedirectUrl } from '@/hooks/use-safe-redirect-url';
import { devEnvLabel, devTabTitle, screenLabel } from '@/lib/branding/dev-tab';
import { resolveHeadBrand } from '@/lib/branding/resolve-head-brand';

/**
 * Per-section title keys (i18n `navigation` namespace). Matched against the
 * current pathname so the browser tab reads `${appName} | ${Section}`
 * (e.g. "Kashu Pay | Orders"). The app uses a component router
 * (`<BrowserRouter>`), so `useMatches`/route `handle` are unavailable — we
 * match the location directly instead. First match wins; unmatched routes
 * (home, admin, partner-admin) fall back to the base suffix.
 */
const PAGE_TITLE_KEYS: ReadonlyArray<{ pattern: string; key: string }> = [
  { pattern: '/org/:orgSlug/orders', key: 'navigation:orders' },
  { pattern: '/org/:orgSlug/subscriptions', key: 'navigation:subscriptions' },
  { pattern: '/org/:orgSlug/payments', key: 'navigation:payments' },
  { pattern: '/org/:orgSlug/customers', key: 'navigation:customers' },
  { pattern: '/org/:orgSlug/processors', key: 'navigation:processors' },
  { pattern: '/org/:orgSlug/payment-flows', key: 'navigation:paymentFlows' },
  { pattern: '/org/:orgSlug/reports', key: 'navigation:reports' },
  { pattern: '/org/:orgSlug/domains', key: 'navigation:domains' },
  { pattern: '/org/:orgSlug/external-requests', key: 'navigation:externalRequests' },
  { pattern: '/org/:orgSlug/billing', key: 'navigation:billing' },
  { pattern: '/org/:orgSlug/settings', key: 'navigation:settings' },
  { pattern: '/org/:orgSlug/profile', key: 'navigation:profile' },
  { pattern: '/org/:orgSlug/setup', key: 'navigation:setup' },
  { pattern: '/org/:orgSlug/stores', key: 'navigation:stores' },
];

/**
 * Auth surfaces whose page BODY re-skins to the partner brand when a partner
 * `redirect_url` is present (they render the partner logo/theme via
 * `useResolveSignInBrand`). The document-head partner override is scoped to these
 * routes so a stray `redirect_url` on an ordinary app route can't flip the tab
 * title/meta while the body stays on the primary brand.
 */
const AUTH_RESKIN_ROUTES: ReadonlyArray<string> = [
  '/sign-in',
  '/accept-sign-in',
  '/accept-invitation',
  '/dashboard/sign-up',
];

const isAuthReskinRoute = (pathname: string): boolean =>
  AUTH_RESKIN_ROUTES.some((path) => matchPath({ path }, pathname) !== null);

const upsertMetaDescription = (content: string): void => {
  let tag = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', 'description');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

/**
 * Drives `document.title` and `<meta name="description">` from the effective
 * brand. Title = `${appName} | ${section ?? baseSuffix}`; recomputed on every
 * navigation (no stale titles). Gated on branding-ready so we never stamp the
 * base brand before the DB overlay resolves on partner hosts. Replaces the
 * bare `useDocumentTitle(brand.appName)` call.
 *
 * On the primary's sign-in hop for a partner satellite (a partner `redirect_url`
 * is present), the page is re-skinned to the partner (logo/theme). We mirror that
 * in the document head so the browser tab reads the partner name too — never
 * "TagadaPay" — matching the visible branding. Off-hop, `signInBrand` is the
 * primary `brandingConfig` and the ambient brand drives the head as usual.
 */
export function useBrandHead(): void {
  const ambient = useAppBranding();
  const ambientReady = useIsAppBrandingReady();
  const { pathname } = useLocation();
  // On the primary's sign-in HOP for a partner satellite (a partner `redirect_url`
  // is present) the auth page is re-skinned to the partner — so drive the head from
  // the partner brand too: the tab title/meta match, never "TagadaPay". Scoped to the
  // re-skin routes (`isAuthReskinRoute`) so a stray `redirect_url` on an ordinary app
  // route can't flip the tab title. Off-hop, the ambient brand drives the head. While
  // the partner's DB branding is still loading, `useResolveSignInBrand` returns a blank
  // neutral brand, so the `appName` guard below defers the stamp instead of flashing a
  // wrong name.
  const redirectUrl = useSafeRedirectUrl();
  const signInBrand = useResolveSignInBrand(redirectUrl);
  const { brand, isReady } = resolveHeadBrand({
    redirectUrl,
    isReskinRoute: isAuthReskinRoute(pathname),
    signInBrand,
    ambient,
    ambientReady,
    base: brandingConfig,
  });

  const { t } = useTranslation();

  const { appName, appDescription } = brand;
  const devStoreName = useDevTabStoreName();

  useEffect(() => {
    // Local and staging tabs read `locl | Orders | My store`; production keeps the brand title.
    const env = devEnvLabel(window.location.hostname);
    if (env) {
      document.title = devTabTitle(env, screenLabel(pathname), devStoreName);
      return;
    }

    if (!isReady || !appName) return;

    const match = PAGE_TITLE_KEYS.find((route) => matchPath({ path: route.pattern, end: false }, pathname));
    const section = match ? t(match.key) : t('common:merchantDashboard');
    document.title = `${appName} | ${section}`;

    if (appDescription) upsertMetaDescription(appDescription);
  }, [isReady, appName, appDescription, pathname, devStoreName, t]);
}
